import { useNavigate } from '@tanstack/react-router';
import { Button } from 'primereact/button';
import { useCallback, useMemo, useState } from 'react';
import { PageContent } from '~/components/ui/layout/page/PageContent';
import { BasicPage } from '~/components/ui/layout/page/basic/BasicPage';
import {
  Workflow as WorkflowTable,
  WorkflowTable as WorkflowTableComponent,
} from '~/components/ui/tables/ag/WorkflowTable';
import { getTaskEntries } from '~/lib/workflows/getTaskEntries';
import { useWorkflows } from '~/lib/workflows/useWorkflows';

import { WorkflowDetailsPanel } from './WorkflowDetailsPanel';
import { WorkflowHeader } from './WorkflowHeader';

type WorkflowViewProps = {
  workflowId?: string;
};

export const WorkflowView = ({ workflowId }: WorkflowViewProps) => {
  console.log('WorkflowView mounted');
  const navigate = useNavigate();

  const [quickFilterText, setQuickFilterText] = useState<string>('');

  const {
    selectedWorkflow,
    isLoading,
    workflows: workflowsState,
  } = useWorkflows({
    workflowId,
  });

  // Get task entries using the utility function
  const taskEntries = useMemo(() => {
    const entries = getTaskEntries(
      selectedWorkflow ? [selectedWorkflow] : workflowsState,
    );
    console.log('Generated task entries:', entries);
    console.log('Selected workflow:', selectedWorkflow);
    console.log('Workflows state:', workflowsState);
    return entries;
  }, [selectedWorkflow, workflowsState]);

  // Properly memoize the handleWorkflowSelect function with an empty dependency array
  // to ensure it doesn't change between renders and cause WorkflowTableComponent to remount
  const handleWorkflowSelect = useCallback(
    (workflow: WorkflowTable) => {
      navigate({
        to: '/workflows/$workflowId',
        params: { workflowId: workflow.workflow_id },
      });
    },
    // Only depend on navigate, which is stable across renders
    [navigate],
  );

  const handleResetFilters = useCallback(() => {
    navigate({ to: '/workflows' });
    setQuickFilterText('');
  }, [navigate]);

  console.log('taskEntries', taskEntries);

  console.log('selectedWorkflow', selectedWorkflow);

  // Memoize the WorkflowTableComponent props to prevent unnecessary remounts
  const workflowTableProps = useMemo(
    () => ({
      onWorkflowSelect: handleWorkflowSelect,
      quickFilterText,
      selectedWorkflow,
    }),
    [handleWorkflowSelect, quickFilterText, selectedWorkflow],
  );

  return (
    <BasicPage title="Workflows">
      <WorkflowHeader selectedWorkflow={selectedWorkflow} loading={isLoading} />

      <PageContent classes="overflow-hidden mt-2">
        <div
          className="flex flex-row gap-2"
          style={{ height: 'calc(100vh - 8rem)', width: '100%' }}
        >
          <div className="flex flex-column" style={{ width: '50%' }}>
            <div
              className="flex flex-column h-full"
              style={{
                borderRadius: '8px',
                backgroundColor: 'var(--bq-color-gray-light2)',
              }}
            >
              <div className="p-3 border-bottom-1 border-300">
                <div className="flex justify-content-between align-items-center mb-2">
                  <h3 className="mt-0 mb-0">Workflows</h3>
                  <Button
                    icon="pi pi-filter-slash"
                    outlined
                    size="small"
                    tooltip="Reset filters"
                    onClick={handleResetFilters}
                  />
                </div>
                <div className="p-inputgroup mb-3">
                  <span className="p-inputgroup-addon">
                    <i className="pi pi-search"></i>
                  </span>
                  <input
                    type="text"
                    className="p-inputtext p-component w-full"
                    placeholder="Search workflows..."
                    value={quickFilterText}
                    onChange={(e) => setQuickFilterText(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex-1 overflow-auto p-3">
                <WorkflowTableComponent {...workflowTableProps} />
              </div>
            </div>
          </div>

          <div className="flex flex-column" style={{ width: '50%' }}>
            <WorkflowDetailsPanel
              selectedWorkflow={selectedWorkflow ?? null}
              taskEntries={taskEntries}
              isLoading={isLoading}
            />
          </div>
        </div>
      </PageContent>
    </BasicPage>
  );
};
